import { ErrorData } from "types/app";

type ResponseType = {
  status: number;
  json: () => Promise<Record<string, unknown>>;
};

type SendApiRequestProps = {
  url?: string;
  json?: boolean;
  authenticationErrorHandler?: (error: ErrorData) => void;
  customErrorHandler?: (error: ErrorData) => void;
  headers?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  method?: string;
  body?: Record<string, unknown>;
  credentials?: string;
  redirect?: string;
};

type BuildRequestParamsProps = Omit<SendApiRequestProps, "authenticationErrorHandler" | "customErrorHandler">;

function apiResponseHandler(
  response: ResponseType,
  successCallback: (response: ResponseType) => void,
  errorCallback: (response: ResponseType | ErrorData) => void,
  crashCallback: (response: ResponseType | ErrorData) => void,
) {
  errorCallback = errorCallback || (() => {});
  crashCallback = crashCallback || (() => {});

  if (response.status >= 200 && response.status < 300) {
    successCallback(response);
  } else if (response.status >= 400 && response.status < 500) {
    errorCallback(response);
  } else {
    crashCallback(response);
  }
}

function handleApiResponse(
  response: ResponseType,
  successHandler: (response: Record<string, unknown>) => void,
  errorHandler: (error: Record<string, unknown> | ErrorData) => void,
) {
  apiResponseHandler(
    response,
    response => {
      response.json().then(
        responseData => {
          successHandler(responseData);
        },
        error => {
          errorHandler(error);
        },
      );
    },
    () => {
      response.json().then(
        responseData => {
          responseData.status = response.status;

          errorHandler(responseData);
        },
        () => errorHandler(response),
      );
    },
    () => {
      errorHandler("Application can't handle server response. Please try again.");
    },
  );
}

function buildRequestUrl(requestParams: BuildRequestParamsProps) {
  const url = requestParams.url;
  const queryParams = requestParams.queryParams;

  let queryString = "";

  if (queryParams) {
    const URLparams = new URLSearchParams(Object.entries(queryParams as Record<string, string>));

    queryString += "?" + URLparams.toString();
  }

  return url + queryString;
}

function buildRequestOptions({
  method,
  json,
  body,
  headers,
  url,
  queryParams,
  ...rest
}: BuildRequestParamsProps): Record<string, unknown> {
  let requestOptions: Record<string, unknown> = { headers };

  if (method && method.toUpperCase() !== "GET") {
    requestOptions = {
      method,
      body: json ? JSON.stringify(body) : body,
      headers,
      // Setting the mode to "cors" explicitly should not be necessary. It was added as an experiment to investigate
      // whether it solves issues with flaky e2e tests (TEAMX-3416).
      mode: "cors",
    };
  }

  return { ...rest, ...requestOptions };
}

function onError(
  reject: (error: Record<string, unknown> | ErrorData) => void,
  requestParams: Record<string, unknown>,
  authenticationErrorHandler?: (error: ErrorData) => void,
  customErrorHandler?: (error: ErrorData) => void,
) {
  return function (error: Record<string, unknown> | ErrorData) {
    if (error && typeof error !== "string" && error.status === 401 && authenticationErrorHandler) {
      authenticationErrorHandler(error as ErrorData);
    }

    if (customErrorHandler) {
      customErrorHandler(error as ErrorData);
    }

    console.error(error, requestParams);

    reject(error);
  };
}

function prepareHeaders(json: boolean, customHeaders: Record<string, unknown>) {
  const headers = {
    ...(customHeaders || {}),
  };

  if (json) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
}

function sendApiRequest({
  json = true,
  authenticationErrorHandler,
  customErrorHandler,
  headers,
  ...requestParams
}: SendApiRequestProps) {
  const params = {
    json,
    headers: prepareHeaders(json || false, headers || {}),
    ...requestParams,
  };

  const url = buildRequestUrl(params);
  const requestOptions = buildRequestOptions(params);

  return new Promise((resolve, reject) => {
    const handleError = onError(reject, requestParams, authenticationErrorHandler, customErrorHandler);

    fetch(url, requestOptions).then(response => handleApiResponse(response, resolve, handleError), handleError);

    return null;
  });
}

export default sendApiRequest;
